// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: simplified Word, Excel and PowerPoint marks and a PDF document, for file cards in
// chats, WhatsApp and mail. Other file types keep their outline icons.

import {useId} from 'react';

export type FileLogoKind = 'word' | 'excel' | 'powerpoint' | 'pdf';

const WORD = /\.(docx?|docm|dotx?|dotm)$/i;
const EXCEL = /\.(xlsx?|xlsm|xlsb|xltx?|xltm|csv)$/i;
const POWERPOINT = /\.(pptx?|pptm|ppsx?|ppsm|potx?|potm)$/i;

/** Which logo a file gets from its name or, failing that, its MIME type; null for anything else. */
export function fileLogoKind(name: string | null | undefined, mime?: string | null): FileLogoKind | null {
	const file = (name ?? '').trim();
	const type = (mime ?? '').toLowerCase().split(';')[0].trim();
	if (/\.pdf$/i.test(file) || type === 'application/pdf') return 'pdf';
	if (WORD.test(file) || type === 'application/msword' || type.includes('wordprocessingml')) return 'word';
	if (EXCEL.test(file) || type === 'application/vnd.ms-excel' || type.includes('spreadsheetml')) return 'excel';
	if (POWERPOINT.test(file) || type === 'application/vnd.ms-powerpoint' || type.includes('presentationml')) {
		return 'powerpoint';
	}
	return null;
}

/** The page behind the letter plate, in four bands like the Microsoft 365 marks. */
function Page({id, bands, grid}: {id: string; bands: [string, string, string, string]; grid?: Array<string>}) {
	return (
		<>
			<clipPath id={id}>
				<rect x="8" y="3" width="22" height="26" rx="2.5" />
			</clipPath>
			<g clipPath={`url(#${id})`}>
				{bands.map((color, index) => (
					<rect key={color + String(index)} x="8" y={3 + index * 6.5} width="22" height="6.5" fill={color} />
				))}
				{grid?.map((color, index) => (
					<rect key={`g${color}${String(index)}`} x="19" y={3 + index * 6.5} width="11" height="6.5" fill={color} />
				))}
			</g>
		</>
	);
}

export function FileTypeLogo({
	kind,
	size = 24,
	className,
}: {
	kind: FileLogoKind;
	size?: number | string;
	className?: string;
}) {
	const id = useId();
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 32 32"
			fill="none"
			aria-hidden="true"
			className={className}
			data-file-logo={kind}
			style={{flexShrink: 0}}
		>
			{kind === 'word' && (
				<>
					<Page id={id} bands={['#41A5EE', '#2B7CD3', '#185ABD', '#103F91']} />
					<rect x="2" y="8" width="16" height="16" rx="2" fill="#185ABD" />
					<path
						d="M5.4 12.2 7.5 19.8 10 13.9l2.5 5.9 2.1-7.6"
						stroke="white"
						strokeWidth="1.7"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</>
			)}
			{kind === 'excel' && (
				<>
					<Page id={id} bands={['#21A366', '#107C41', '#185C37', '#185C37']} grid={['#33C481', '#21A366', '#107C41']} />
					<rect x="2" y="8" width="16" height="16" rx="2" fill="#107C41" />
					<path d="m6.4 12.2 7.2 7.6m0-7.6-7.2 7.6" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
				</>
			)}
			{kind === 'powerpoint' && (
				<>
					<circle cx="18" cy="16" r="13" fill="#ED6C47" />
					<path d="M18 16V3a13 13 0 0 1 13 13H18Z" fill="#FF8F6B" />
					<path d="M18 16h13a13 13 0 0 1-13 13V16Z" fill="#D35230" />
					<rect x="2" y="8" width="16" height="16" rx="2" fill="#C43E1C" />
					<path
						d="M7.4 19.8v-7.6h3.2a2.4 2.4 0 0 1 0 4.8H7.4"
						stroke="white"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</>
			)}
			{kind === 'pdf' && (
				<>
					<path d="M8.5 2.5h11.2L27 9.8v17.7a2 2 0 0 1-2 2H8.5a2 2 0 0 1-2-2v-23a2 2 0 0 1 2-2Z" fill="#E5252A" />
					<path d="M19.7 2.5v5.3a2 2 0 0 0 2 2H27Z" fill="#F7A3A4" />
					<rect x="3" y="15" width="19" height="9" rx="1.8" fill="#B30B00" />
					<text
						x="12.5"
						y="21.9"
						fill="white"
						fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
						fontSize="6.6"
						fontWeight="800"
						textAnchor="middle"
						letterSpacing="0.2"
					>
						PDF
					</text>
				</>
			)}
		</svg>
	);
}
